/**
 * Analytics Customization Dialog
 * Allows users to show/hide and reorder summary cards and tabs
 */

import { useState } from "react";
import { Settings, GripVertical, Eye, EyeOff, RotateCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

const DraggableItem = ({ item, index, onDragStart, onDragOver, onDrop, isHidden, onToggleVisibility }) => {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={(e) => onDrop(e, index)}
      className={`flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-move border border-transparent hover:border-border transition-colors ${isHidden ? 'opacity-50' : ''}`}
    >
      <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      <span className="flex-1 font-medium">{item.label || item.key}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility(item.id || item.key);
        }}
        className="h-8 w-8 p-0"
      >
        {isHidden ? (
          <EyeOff className="w-4 h-4 text-muted-foreground" />
        ) : (
          <Eye className="w-4 h-4 text-primary" />
        )}
      </Button>
    </div>
  );
};

const DraggableList = ({ items, hiddenItems, onReorder, onToggleVisibility }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newOrder = [...items];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);
    
    onReorder(newOrder.map(item => item.id || item.key));
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <DraggableItem
          key={item.id || item.key}
          item={item}
          index={index}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          isHidden={hiddenItems.includes(item.id || item.key)}
          onToggleVisibility={onToggleVisibility}
        />
      ))}
    </div>
  );
};

const AnalyticsCustomizeDialog = ({
  summaryCards,
  tabs,
  hiddenSummaryCards,
  hiddenTabs,
  onReorderSummaryCards,
  onToggleSummaryCard,
  onReorderTabs,
  onToggleTab,
  onReset,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="customize-dashboard-btn">
          <Settings className="w-4 h-4 mr-2" />
          Customize
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Customize Dashboard</DialogTitle>
          <DialogDescription>
            Drag items to reorder. Click the eye icon to show/hide.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="cards" className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="cards">Summary Cards</TabsTrigger>
            <TabsTrigger value="tabs">Tabs</TabsTrigger>
          </TabsList>

          <TabsContent value="cards" className="mt-4">
            <Label className="text-sm text-muted-foreground mb-3 block">
              Drag to reorder summary cards at the top of the page
            </Label>
            <DraggableList
              items={summaryCards}
              hiddenItems={hiddenSummaryCards}
              onReorder={onReorderSummaryCards}
              onToggleVisibility={onToggleSummaryCard}
            />
          </TabsContent>

          <TabsContent value="tabs" className="mt-4">
            <Label className="text-sm text-muted-foreground mb-3 block">
              Drag to reorder analytics tabs
            </Label>
            <DraggableList
              items={tabs}
              hiddenItems={hiddenTabs}
              onReorder={onReorderTabs}
              onToggleVisibility={onToggleTab}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button onClick={() => setOpen(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnalyticsCustomizeDialog;
